import { useMutation, useQuery } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import { availableGroups, isValidGroup } from "../../api/Login";
import styles from "./GroupSelector.module.css";
import { useCallback, useEffect, useState } from "react";
import { LandingPage } from "../LandingPage/LandingPage";
import { renderError } from "../../common/helperfunctions/ApiError";

interface Opts {
  group: string;
}

export const GroupSelectionPage = () => {
  // const isDevel = !process.env.NODE_ENV || process.env.NODE_ENV === "development";
  const history = useHistory();
  const { data: groups, status } = useQuery({
    queryKey: ["groups"],
    queryFn: async ({ signal }) => await availableGroups(signal),
  });

  const { mutateAsync: validateGroup } = useMutation<boolean, unknown, Opts>(
    (options: Opts) => {
      return isValidGroup(options.group);
    },
    {
      onError: (err, variables) => {
        renderError(err);
      },
    }
  );

  const [groupValue, setGroupValue] = useState<string | undefined>();
  const [isValid, setIsValid] = useState<boolean | undefined>();

  useEffect(() => {
    if (status === "success" && Array.isArray(groups) && !!groups.length) {
      setGroupValue(groups?.[0].name);
    }
    return () => setGroupValue(undefined);
  }, [groups, status]);

  const validate = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (groupValue && Array.isArray(groups)) {
        // Case 1: API returns groups
        if (!!groups.length && groups.find((g) => g.name === groupValue)) {
          setIsValid(true);
        } else if (!groups.length) {
          // Case 2: API returns empty array (e.g. disable_public_groups feature flag)
          var isValid = await validateGroup({ group: groupValue });
          if (isValid === true) {
            // queryClient.setQueryData(
            //   ["groups"],
            //   [
            //     {
            //       name: groupValue,
            //       edition: "Unknown",
            //     },
            //   ]
            // ); // in case of no groups (e.g. disable_public_groups feature flag), add the one entered
            setIsValid(true);
          } else {
            setIsValid(false);
            // queryClient.invalidateQueries({ queryKey: ["groups"] });
          }
        } else {
          setIsValid(false);
        }
      }
    },
    [groupValue, groups, validateGroup]
  );

  useEffect(() => {
    if (isValid) {
      history.push(`/${groupValue}`);
    }
  }, [groupValue, history, isValid]);

  return (
    <LandingPage>
      <form onSubmit={async (e) => await validate(e)}>
        <div className="input-group">
          {groups && Array.isArray(groups) && !!groups.length ? (
            <select
              id="company"
              className="form-control"
              defaultValue={groups?.[0].name}
              onChange={(e) => setGroupValue(e.target.value)}
              onKeyDown={async (e) => e.key === "Enter" && (await validate(e as any))}
            >
              {groups?.map((g, i) => (
                <option key={i} value={g.name}>
                  {g.name}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              className={`form-control ${isValid === false && "border-danger"}`}
              placeholder="Group Name"
              onChange={(e) => setGroupValue(e.target.value)}
            />
          )}
          <span className="input-group-btn">
            <button className="btn btn-default" type="submit" disabled={!groupValue}>
              Go!
            </button>
          </span>
        </div>
        {isValid === false && <div className={styles.formfield_error}>A valid group name is required</div>}
      </form>
    </LandingPage>
  );
};
